// ── APEX PREDATOR ── all tunable values live here. No magic numbers elsewhere.

// ─── Grid / world ───────────────────────────────────────────────────────────
export const TILE_X = 2.4;          // horizontal hop distance
export const ROW_DEPTH = 3.0;       // forward hop distance (one lane)
export const FIELD_TILES_X = 19;    // playfield width in tiles (odd → has a centre)
export const FIELD_HALF_TILES = (FIELD_TILES_X - 1) / 2;
export const FIELD_HALF = (FIELD_TILES_X * TILE_X) / 2;
export const WRAP_MARGIN = 16;      // how far off-field trains spawn/despawn
export const WRAP_HALF = FIELD_HALF + WRAP_MARGIN;

export const ROW = { GRASS: 'grass', RAIL: 'rail', FREIGHT: 'freight' };

// ─── Rendering ──────────────────────────────────────────────────────────────
export const RENDER = {
  PIXEL_SCALE: 3,         // render at 1/3 res, upscale with image-rendering:pixelated
  MAX_PIXEL_RATIO: 1,     // pixel art wants exactly one device pixel per game pixel
  CLEAR_COLOR: 0x5c94fc,   // NES sky — the treeline closes the horizon onto it
};

export const CAMERA = {
  // The rig sits on a fixed line of sight and slides along it until the
  // playfield width fits, so the framing survives any aspect ratio.
  HEIGHT: 38,             // shape of the view direction, not an absolute height
  BACK: 17,
  LOOK_AHEAD: 9,          // aim point this far in front of the player
  FOV_LANDSCAPE: 40,
  FOV_PORTRAIT: 58,
  VIEW_HALF_WIDTH: 25,    // world units that must stay on screen either side
  DIST_RANGE: [46, 86],
  NEAR: 1,
  FAR: 260,
  FOLLOW_LERP: 6.5,       // per-second smoothing rate
  START_LIFT: 16,         // extra height during the level intro sweep
};

// ─── Player ─────────────────────────────────────────────────────────────────
export const PLAYER = {
  HOP_DURATION: 0.135,
  HOP_HEIGHT: 1.0,
  HALF_WIDTH: 0.55,       // collision half-extent on x
  HALF_DEPTH: 0.55,
  GROUND_Y: 0,
  DECK_Y: 0.92,           // standing height when riding a flatcar
  QUEUE_WINDOW: 0.09,     // buffer a hop this long before the current one ends
  SPAWN_INVULN: 1.1,      // seconds of grace after respawning
  BLINK_HZ: 9,
};

export const LIVES_START = 3;

// ─── Rolling stock ──────────────────────────────────────────────────────────
export const RAILCAR = {
  LENGTH: 7.2,
  WIDTH: 2.4,
  BODY_H: 1.32,
  FLOOR_Y: 0.62,          // underside of the body above the railhead
  GAP: 0.35,              // coupling gap between cars in a consist
};

export const FLATCAR = {
  LENGTH: 9.0,
  WIDTH: 2.4,
  DECK_Y: 0.92,
  WALL_H: 0.34,
  GAP: 0.5,
};

// ─── Lane balance ───────────────────────────────────────────────────────────
export const LANE = {
  BASE_SPEED: 7.0,
  SPEED_PER_LEVEL: 0.55,
  SPEED_CAP: 17.0,
  SPEED_JITTER: [0.75, 1.35],     // per-lane multiplier range
  FREIGHT_SPEED_SCALE: 0.55,

  RAIL_CONSISTS: [2, 3],          // consists per lane, [min,max]
  RAIL_CARS: [1, 3],
  RAIL_MIN_GAP: 9.0,              // clear world units between consists

  FREIGHT_CONSISTS: [2, 2],       // two rakes per yard road keeps gaps readable
  FREIGHT_CARS: [2, 3],
  FREIGHT_MIN_GAP: 8.0,           // rideable lanes need reachable decks
  FREIGHT_GAP_TIGHTEN: 0.9,       // level scaling on freight deck coverage
  MIN_GAP_FLOOR: 5.0,
};

// ─── Level shape ────────────────────────────────────────────────────────────
export const LEVEL = {
  RAIL_A: [4, 6],         // lower rail block: [level-1 count, max count]
  FREIGHT_COUNT: [3, 4],
  RAIL_B: [2, 3],         // rail block above the yard, below the goal
  MEDIAN_ROWS: 1,
};

// ─── Cobra Hazard ────────────────────────────────────────────────────────────
export const COBRA = {
  INTERVAL_MIN: 3.5,      // min time between cobra appearances
  INTERVAL_MAX: 7.0,      // max time between cobra appearances
  WARN_DURATION: 0.6,     // seconds cobra peeks/hisses before becoming deadly
  ACTIVE_DURATION: 4.0,   // seconds cobra stays fully upright & deadly
  RETREAT_DURATION: 0.5,  // seconds cobra takes to submerge back into grass
  HIT_RADIUS: 0.95,       // collision radius
  SLITHER_SPEED: 6.0,     // speed at which cobra slithers across grass lane
  SLITHER_CHANCE: 0.75,   // probability cobra slithers across vs pops up stationary
};

// ─── Flood Hazard ────────────────────────────────────────────────────────────
export const FLOOD = {
  INTERVAL_MIN: 18.0,     // min seconds between flood blasts
  INTERVAL_MAX: 32.0,     // max seconds between flood blasts
  WARN_DURATION: 1.4,     // siren warning duration
  SPEED: 44.0,            // flood surge speed along tracks
  ROW_SPAN: 3,            // number of rows swept by flood
};

// ─── Electrified Yard Visuals ────────────────────────────────────────────────
export const ELECTRIC = {
  INTERVAL_MIN: 0.3,      // min seconds between electrical zaps
  INTERVAL_MAX: 1.4,      // max seconds between electrical zaps
  ZAP_DURATION: 0.12,     // duration of an electric zap
};

// ─── Trailing Kids / Family ──────────────────────────────────────────────────
export const KIDS = {
  START_COUNT: 0,        // initial number of trailing kids on game start
  PER_CROSSING: 1,       // kids added per successful level crossing
  MAX_COUNT: 10,         // maximum trailing kids allowed in procession
  CHECK_COLLISION: 1,    // 1 = kids are vulnerable / hitboxes active, 0 = kids invulnerable
};

// ─── Curry Item ─────────────────────────────────────────────────────────────
export const CURRY = {
  CHANCE: 0.20,          // 20% chance curry bowl spawns per level (rare item)
  IMMUNITY_CHANCE: 0.90, // 90% chance grants immunity
  IMMUNITY_DUR_MIN: 3.0, // min immunity duration (seconds)
  IMMUNITY_DUR_MAX: 5.0, // max immunity duration (seconds)
  POISON_CHANCE: 0.10,   // 10% chance poisons character
  POISON_DUR_MIN: 3.0,   // min poison duration (seconds)
  POISON_DUR_MAX: 5.0,   // max poison duration (seconds)
  SLOW_FACTOR: 1.8,      // hop speed slow multiplier during poison
};

// ─── Scoring ────────────────────────────────────────────────────────────────
export const SCORE = {
  ADVANCE: 10,            // per newly-reached row
  GOAL: 200,
  LEVEL_BONUS: 100,       // × level
  KID_BONUS: 500,         // per intact child reaching the goal bank
  TIME_BONUS_PER_SEC: 5,
  RUN_TIME_LIMIT: 45,     // seconds per crossing before the bonus is gone
};

// ─── Feel / juice ───────────────────────────────────────────────────────────
export const JUICE = {
  SHAKE_DEATH: 1.6,
  SHAKE_DECAY: 3.4,
  DEATH_FREEZE: 1.35,     // seconds before respawn
  LEVEL_FLASH: 0.5,
  SMOOSH_DURATION: 0.18,  // seconds the player takes to flatten on death
  GORE_GRAVITY: 32,       // fall speed of the scattered body parts
  GORE_DRAG: 0.4,         // per-second air drag on flying parts
  GORE_MAX_PARTS: 600,    // oldest parts vanish past this many on the field
  SIGNAL_LEAD: 1.4,       // seconds of warning before a consist enters the field
  SIGNAL_BLINK_HZ: 4.5,
};

// ─── Palette (NES-ish, hard and saturated) ──────────────────────────────────
export const COLORS = {
  GRASS_A: 0x1d8b26,
  GRASS_B: 0x14691c,
  GRASS_EDGE: 0x0a4210,
  BUSH: 0x063a0c,
  MEDIAN: 0x186b2a,

  BALLAST: 0x121014,
  SLEEPER: 0x4a2f1c,
  RAIL: 0xb8c0c6,

  YARD_DECK: 0xf2c015,
  YARD_STRIPE: 0x141414,
  YARD_PIT: 0x0a0a0c,

  TRAIN: [0xe32219, 0xe230cf, 0x1f7fe0],
  TRAIN_ROOF: 0xb9c2c7,
  TRAIN_SKIRT: 0x1a1d21,
  WINDOW: 0xffe24d,
  HEADLIGHT: 0xfff3a0,
  WHEEL: 0x33383f,
  CHASSIS: 0x22262b,

  FLATCAR: 0x3d434a,
  FLATCAR_DECK: 0x2a2e33,
  FLATCAR_TRIM: 0xf2c015,

  COBRA_BODY: 0x265c1b,
  COBRA_BELLY: 0xdfb435,
  COBRA_MARKING: 0x10200a,
  COBRA_EYE: 0xe32219,
  COBRA_TONGUE: 0xdd1e1e,

  WATER_DEEP: 0x1d4ed8,
  WATER_MID: 0x3b82f6,
  WATER_FOAM: 0xe0f2fe,

  ELECTRIC_ZAP: 0x7dd3fc,
  ELECTRIC_GLOW: 0x38bdf8,

  SKIN: 0xe8b083,
  HAIR: 0x5a3418,
  SHIRT: 0x1f7fe0,
  TROUSERS: 0x1d2440,
  SHOE: 0x161616,

  SIGNAL_OFF: 0x3a1010,
  SIGNAL_ON: 0xff2a1a,
  TUNNEL: 0x080809,

  AMBIENT: 0xb9c9d6,
  SUN: 0xfff6e0,
};

export const LIGHT = {
  AMBIENT_INTENSITY: 1.5,
  SUN_INTENSITY: 1.35,
  SUN_POS: [-24, 46, 26],
};

// ─── Audio (all synthesised, no asset files) ────────────────────────────────
export const AUDIO = {
  MASTER: 0.28,
  HOP: { freq: 620, to: 880, dur: 0.07, type: 'square' },
  BUMP: { freq: 180, to: 120, dur: 0.08, type: 'square' },
  DEATH: { freq: 340, to: 55, dur: 0.55, type: 'sawtooth' },
  BOARD: { freq: 420, to: 560, dur: 0.06, type: 'square' },
  GOAL: [523, 659, 784, 1047],
  GAME_OVER: [392, 330, 262, 196],
};

// ─── Play.fun safe insets ───────────────────────────────────────────────────
function readInset(name) {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name);
  return parseInt(v) || 0;
}
export const SAFE_ZONE = {
  TOP_PX: Math.max(75, readInset('--ogp-safe-top-inset')),
  BOTTOM_PX: readInset('--ogp-safe-bottom-inset'),
};

// ─── Tuning panel ───────────────────────────────────────────────────────────
// Schema for the in-game config panel (toggle with ` or the CONFIG button).
// Each entry addresses a live property of the objects above by dot path — the
// path doubles as the persistence key. mode:
//   live  — read every frame / at event time, applies instantly
//   fit   — applies once the camera rig refits (the panel triggers a refit)
//   build — baked into lane/level planning, applies on the next level rebuild
const TUNING_ROOTS = { PLAYER, LANE, LEVEL, CAMERA, SCORE, JUICE, AUDIO, COBRA, FLOOD, ELECTRIC, KIDS, CURRY };

export const TUNING = [
  { path: 'CURRY.CHANCE', section: 'CURRY BOWL', label: 'Spawn chance', min: 0, max: 1.0, step: 0.05, mode: 'build' },
  { path: 'CURRY.IMMUNITY_CHANCE', section: 'CURRY BOWL', label: 'Immunity chance', min: 0, max: 1.0, step: 0.05, mode: 'live' },
  { path: 'CURRY.IMMUNITY_DUR_MIN', section: 'CURRY BOWL', label: 'Immunity min (s)', min: 1, max: 10, step: 0.5, mode: 'live' },
  { path: 'CURRY.IMMUNITY_DUR_MAX', section: 'CURRY BOWL', label: 'Immunity max (s)', min: 1, max: 10, step: 0.5, mode: 'live' },
  { path: 'CURRY.POISON_CHANCE', section: 'CURRY BOWL', label: 'Poison chance', min: 0, max: 1.0, step: 0.05, mode: 'live' },
  { path: 'CURRY.POISON_DUR_MIN', section: 'CURRY BOWL', label: 'Poison min (s)', min: 1, max: 10, step: 0.5, mode: 'live' },
  { path: 'CURRY.POISON_DUR_MAX', section: 'CURRY BOWL', label: 'Poison max (s)', min: 1, max: 10, step: 0.5, mode: 'live' },
  { path: 'CURRY.SLOW_FACTOR', section: 'CURRY BOWL', label: 'Poison slow factor', min: 1.1, max: 3.0, step: 0.1, mode: 'live' },

  { path: 'KIDS.START_COUNT', section: 'KIDS / FAMILY', label: 'Start kids', min: 0, max: 10, step: 1, mode: 'live' },
  { path: 'KIDS.PER_CROSSING', section: 'KIDS / FAMILY', label: 'Kids per crossing', min: 0, max: 5, step: 1, mode: 'live' },
  { path: 'KIDS.MAX_COUNT', section: 'KIDS / FAMILY', label: 'Max kids limit', min: 1, max: 20, step: 1, mode: 'live' },
  { path: 'KIDS.CHECK_COLLISION', section: 'KIDS / FAMILY', label: 'Vulnerable kids (1/0)', min: 0, max: 1, step: 1, mode: 'live' },

  { path: 'PLAYER.HOP_DURATION', section: 'PLAYER', label: 'Hop time', min: 0.06, max: 0.35, step: 0.005, mode: 'live' },
  { path: 'PLAYER.HOP_HEIGHT', section: 'PLAYER', label: 'Hop height', min: 0.3, max: 2.5, step: 0.05, mode: 'live' },
  { path: 'PLAYER.QUEUE_WINDOW', section: 'PLAYER', label: 'Input buffer', min: 0, max: 0.3, step: 0.01, mode: 'live' },
  { path: 'PLAYER.SPAWN_INVULN', section: 'PLAYER', label: 'Spawn grace', min: 0, max: 3, step: 0.05, mode: 'live' },
  { path: 'PLAYER.HALF_WIDTH', section: 'PLAYER', label: 'Hitbox half-width', min: 0.3, max: 0.9, step: 0.01, mode: 'live' },
  { path: 'PLAYER.BLINK_HZ', section: 'PLAYER', label: 'Blink rate', min: 1, max: 20, step: 0.5, mode: 'live' },

  { path: 'LANE.BASE_SPEED', section: 'TRAFFIC', label: 'Base speed', min: 1, max: 20, step: 0.1, mode: 'build' },
  { path: 'LANE.SPEED_PER_LEVEL', section: 'TRAFFIC', label: 'Speed per level', min: 0, max: 2, step: 0.05, mode: 'build' },
  { path: 'LANE.SPEED_CAP', section: 'TRAFFIC', label: 'Speed cap', min: 5, max: 30, step: 0.5, mode: 'build' },
  { path: 'LANE.SPEED_JITTER.0', section: 'TRAFFIC', label: 'Jitter min', min: 0.4, max: 1, step: 0.01, mode: 'build' },
  { path: 'LANE.SPEED_JITTER.1', section: 'TRAFFIC', label: 'Jitter max', min: 1, max: 2, step: 0.01, mode: 'build' },
  { path: 'LANE.FREIGHT_SPEED_SCALE', section: 'TRAFFIC', label: 'Yard speed ×', min: 0.2, max: 1, step: 0.01, mode: 'build' },
  { path: 'LANE.RAIL_MIN_GAP', section: 'TRAFFIC', label: 'Rail min gap', min: 4, max: 20, step: 0.5, mode: 'build' },
  { path: 'LANE.FREIGHT_MIN_GAP', section: 'TRAFFIC', label: 'Yard min gap', min: 3, max: 16, step: 0.5, mode: 'build' },
  { path: 'LANE.MIN_GAP_FLOOR', section: 'TRAFFIC', label: 'Gap floor', min: 2, max: 10, step: 0.5, mode: 'build' },

  { path: 'LEVEL.RAIL_A.0', section: 'LEVEL', label: 'Rail block base', min: 1, max: 8, step: 1, mode: 'build' },
  { path: 'LEVEL.RAIL_A.1', section: 'LEVEL', label: 'Rail block max', min: 1, max: 10, step: 1, mode: 'build' },
  { path: 'LEVEL.FREIGHT_COUNT.0', section: 'LEVEL', label: 'Yard roads base', min: 1, max: 6, step: 1, mode: 'build' },
  { path: 'LEVEL.FREIGHT_COUNT.1', section: 'LEVEL', label: 'Yard roads max', min: 1, max: 8, step: 1, mode: 'build' },
  { path: 'LEVEL.RAIL_B.0', section: 'LEVEL', label: 'Top rail base', min: 0, max: 6, step: 1, mode: 'build' },
  { path: 'LEVEL.RAIL_B.1', section: 'LEVEL', label: 'Top rail max', min: 0, max: 8, step: 1, mode: 'build' },

  { path: 'COBRA.INTERVAL_MIN', section: 'COBRA', label: 'Min interval', min: 1, max: 20, step: 0.5, mode: 'live' },
  { path: 'COBRA.INTERVAL_MAX', section: 'COBRA', label: 'Max interval', min: 2, max: 30, step: 0.5, mode: 'live' },
  { path: 'COBRA.WARN_DURATION', section: 'COBRA', label: 'Warning time', min: 0.2, max: 3.0, step: 0.1, mode: 'live' },
  { path: 'COBRA.ACTIVE_DURATION', section: 'COBRA', label: 'Active time', min: 1, max: 10, step: 0.5, mode: 'live' },
  { path: 'COBRA.RETREAT_DURATION', section: 'COBRA', label: 'Retreat time', min: 0.2, max: 2.0, step: 0.1, mode: 'live' },
  { path: 'COBRA.HIT_RADIUS', section: 'COBRA', label: 'Hit radius', min: 0.3, max: 2.5, step: 0.05, mode: 'live' },
  { path: 'COBRA.SLITHER_SPEED', section: 'COBRA', label: 'Slither speed', min: 1, max: 20, step: 0.5, mode: 'live' },
  { path: 'COBRA.SLITHER_CHANCE', section: 'COBRA', label: 'Slither chance', min: 0, max: 1.0, step: 0.05, mode: 'live' },

  { path: 'FLOOD.INTERVAL_MIN', section: 'RIVER FLOOD', label: 'Min interval', min: 5, max: 60, step: 1, mode: 'live' },
  { path: 'FLOOD.INTERVAL_MAX', section: 'RIVER FLOOD', label: 'Max interval', min: 10, max: 90, step: 1, mode: 'live' },
  { path: 'FLOOD.WARN_DURATION', section: 'RIVER FLOOD', label: 'Warning time', min: 0.5, max: 5.0, step: 0.1, mode: 'live' },
  { path: 'FLOOD.SPEED', section: 'RIVER FLOOD', label: 'Surge speed', min: 15, max: 100, step: 1, mode: 'live' },
  { path: 'FLOOD.ROW_SPAN', section: 'RIVER FLOOD', label: 'Rows swept', min: 1, max: 6, step: 1, mode: 'live' },

  { path: 'ELECTRIC.INTERVAL_MIN', section: 'ELECTRIFIED YARD', label: 'Min interval', min: 0.1, max: 2.0, step: 0.05, mode: 'live' },
  { path: 'ELECTRIC.INTERVAL_MAX', section: 'ELECTRIFIED YARD', label: 'Max interval', min: 0.3, max: 4.0, step: 0.1, mode: 'live' },
  { path: 'ELECTRIC.ZAP_DURATION', section: 'ELECTRIFIED YARD', label: 'Zap duration', min: 0.05, max: 0.5, step: 0.01, mode: 'live' },

  { path: 'CAMERA.HEIGHT', section: 'CAMERA', label: 'View height', min: 20, max: 60, step: 1, mode: 'fit' },
  { path: 'CAMERA.BACK', section: 'CAMERA', label: 'View back', min: 5, max: 30, step: 1, mode: 'fit' },
  { path: 'CAMERA.LOOK_AHEAD', section: 'CAMERA', label: 'Look ahead', min: 0, max: 20, step: 0.5, mode: 'fit' },
  { path: 'CAMERA.FOLLOW_LERP', section: 'CAMERA', label: 'Follow speed', min: 1, max: 15, step: 0.1, mode: 'live' },
  { path: 'CAMERA.FOV_LANDSCAPE', section: 'CAMERA', label: 'FOV wide', min: 25, max: 70, step: 1, mode: 'fit' },
  { path: 'CAMERA.FOV_PORTRAIT', section: 'CAMERA', label: 'FOV tall', min: 30, max: 80, step: 1, mode: 'fit' },
  { path: 'CAMERA.VIEW_HALF_WIDTH', section: 'CAMERA', label: 'Fit half-width', min: 15, max: 35, step: 0.5, mode: 'fit' },
  { path: 'CAMERA.START_LIFT', section: 'CAMERA', label: 'Intro lift', min: 0, max: 30, step: 0.5, mode: 'live' },

  { path: 'SCORE.ADVANCE', section: 'SCORING', label: 'Row points', min: 0, max: 50, step: 1, mode: 'live' },
  { path: 'SCORE.GOAL', section: 'SCORING', label: 'Goal bonus', min: 0, max: 1000, step: 10, mode: 'live' },
  { path: 'SCORE.LEVEL_BONUS', section: 'SCORING', label: 'Level bonus ×', min: 0, max: 500, step: 10, mode: 'live' },
  { path: 'SCORE.KID_BONUS', section: 'SCORING', label: 'Kid bonus /child', min: 0, max: 2000, step: 50, mode: 'live' },
  { path: 'SCORE.TIME_BONUS_PER_SEC', section: 'SCORING', label: 'Time bonus /s', min: 0, max: 20, step: 1, mode: 'live' },
  { path: 'SCORE.RUN_TIME_LIMIT', section: 'SCORING', label: 'Time limit', min: 10, max: 120, step: 5, mode: 'live' },

  { path: 'JUICE.SHAKE_DEATH', section: 'JUICE', label: 'Death shake', min: 0, max: 3, step: 0.05, mode: 'live' },
  { path: 'JUICE.SHAKE_DECAY', section: 'JUICE', label: 'Shake decay', min: 0.5, max: 8, step: 0.1, mode: 'live' },
  { path: 'JUICE.DEATH_FREEZE', section: 'JUICE', label: 'Death freeze', min: 0.1, max: 3, step: 0.05, mode: 'live' },
  { path: 'JUICE.SMOOSH_DURATION', section: 'JUICE', label: 'Smoosh time', min: 0.05, max: 0.6, step: 0.01, mode: 'live' },
  { path: 'JUICE.GORE_GRAVITY', section: 'JUICE', label: 'Gore gravity', min: 2, max: 60, step: 1, mode: 'live' },
  { path: 'JUICE.GORE_DRAG', section: 'JUICE', label: 'Gore drag', min: 0, max: 3, step: 0.05, mode: 'live' },
  { path: 'JUICE.GORE_MAX_PARTS', section: 'JUICE', label: 'Max gore parts', min: 50, max: 1200, step: 50, mode: 'live' },
  { path: 'JUICE.SIGNAL_LEAD', section: 'JUICE', label: 'Signal lead', min: 0, max: 3, step: 0.05, mode: 'live' },
  { path: 'JUICE.SIGNAL_BLINK_HZ', section: 'JUICE', label: 'Signal blink', min: 1, max: 10, step: 0.5, mode: 'live' },
  { path: 'JUICE.LEVEL_FLASH', section: 'JUICE', label: 'Level flash', min: 0, max: 1.5, step: 0.05, mode: 'live' },

  { path: 'AUDIO.MASTER', section: 'AUDIO', label: 'Master volume', min: 0, max: 1, step: 0.01, mode: 'live' },
];

function resolvePath(path) {
  const seg = path.split('.');
  let obj = TUNING_ROOTS;
  for (let i = 0; i < seg.length - 1; i++) obj = obj?.[seg[i]];
  if (obj == null) throw new Error(`Unknown tuning path: ${path}`);
  return { obj, key: seg[seg.length - 1] };
}

export function getTunable(path) {
  const { obj, key } = resolvePath(path);
  return obj[key];
}

export function setTunable(path, value) {
  const { obj, key } = resolvePath(path);
  obj[key] = value;
  if (path === 'CURRY.IMMUNITY_CHANCE') {
    CURRY.POISON_CHANCE = Math.max(0, Math.min(1.0, 1.0 - value));
  } else if (path === 'CURRY.POISON_CHANCE') {
    CURRY.IMMUNITY_CHANCE = Math.max(0, Math.min(1.0, 1.0 - value));
  }
}

export const CONFIG_STORAGE_KEY = 'apex-config-v1';

// Snapshot before any override lands, so RESET can always get back here.
export const CONFIG_DEFAULTS = Object.fromEntries(
  TUNING.map((t) => [t.path, getTunable(t.path)]),
);

// Saved overrides apply at module load, before any class snapshots a value.
try {
  const saved = JSON.parse(localStorage.getItem(CONFIG_STORAGE_KEY) || '{}');
  for (const t of TUNING) {
    if (typeof saved[t.path] === 'number') setTunable(t.path, saved[t.path]);
  }
} catch { /* storage unavailable (sandboxed embed) — run on defaults */ }
